import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import ApiService from '../../_services/ApiService';
import ConflictGroupService, { ConflictGroup } from '../../_services/ConflictGroupService';
import WeekPatternGrid from './components/WeekPatternGrid';
import LinearSlotList from './components/LinearSlotList';
import RollbackPanel from './components/RollbackPanel';
import ConfirmModal from './components/ConfirmModal';
import AlertModal from './components/AlertModal';
import ExcelImportModal from './components/ExcelImportModal';
import TemplateBirdsEyeView from './components/TemplateBirdsEyeView';
import TemplateAnalytics from './components/TemplateAnalytics';
import TemplateConductedView from './components/TemplateConductedView';
import SlotFiltersPanel from './components/SlotFiltersPanel';
import SubstituteModal from '../../components/SubstituteModal';
import {
  Substitution,
  listSubstitutions,
} from '../../_services/SubstitutionService';
import MacroPlanningService, { MacroPlanConfig } from '../../_services/MacroPlanningService';
import './styles.scss';

// Meeting type constants
const ONLINE_MEETING_ZOOM = 44;
const ONLINE_MEETING_TEAMS = 45;
const ONLINE_MEETING_WEBINAR = 46;

interface ActivitySlot {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  instructorId: number;
  instructorName: string;
  topicMasterId: number;
  topicName: string;
  agenda: string;
  batchIds: number[];
  zoomMeetingId: string | null;
  onlineMeetingType?: number | null;
  roomId?: number;
  roomName?: string;
  centreId?: number;
  centreName?: string;
  confirmedByUserId?: number | null;
  confirmedAt?: string | null;
  substitution?: {
    id: number;
    status: string;
    substituteInstructorId: number | null;
    substituteInstructorName: string | null;
    substituteTopicId: number | null;
    substituteTopicName: string | null;
    substituteAgenda: string | null;
    reason: string | null;
  } | null;
}

interface MacroPlanSchedulingConfig {
  sessionDurationMinutes?: number;
  minDaysBetween?: number;
  maxPerDay?: number;
  maxPerWeek?: number;
  preferredDays?: string[];
}

interface MacroPlan {
  inputId: number;
  moduleId: number | null;
  moduleCode: string | null;
  moduleName: string | null;
  sessions: number | null;
  plannedCapacity: number | null;
  studentGroup: string | null;
  schedulingConfig: MacroPlanSchedulingConfig | null;
}

interface Template {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  activitySlotType: number;
  activeStatus: number;
  lockStatus: number;
  yearName: string;
  programId: number | null;
  programName: string;
  courseName: string;
  termName: string;
  remarks: string;
  config: string | null;
  activitySlotTemplates: ActivitySlot[];
  batchMappings: any[];
  macroPlans?: MacroPlan[];
  sourceBoardId?: number | null;
  publishStatus?: "Draft" | "Published" | "Unpublished" | null;
  publishedByUserId?: number | null;
  publishedAt?: string | null;
}

interface TimetablePermissions {
  canTakeAttendance: boolean;
  canEditTemplate: boolean;
  canDeleteTemplate: boolean;
  canCreateTemplate: boolean;
}

const TemplateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isEditMode = searchParams.get('edit') === 'true';

  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [editedName, setEditedName] = useState('');
  const [editedRemarks, setEditedRemarks] = useState('');
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState<ActivitySlot | null>(null);
  const [substituteSlot, setSubstituteSlot] = useState<ActivitySlot | null>(null);
  const [substitutions, setSubstitutions] = useState<Substitution[]>([]);

  // Two attendance entry points. Java legacy is the canonical "Attendance"
  // button; the "Attendance 2" button hits the new Node route at
  // /attendance-node/* for side-by-side validation. Remove the Java helper
  // and `onAttendance` callback once the Node port is fully verified.
  const buildAttendanceQuery = (
    templateSlotId: number,
    instructorId?: number,
    instructorName?: string,
  ): string => {
    const params = new URLSearchParams();
    params.set('templateSlotId', String(templateSlotId));
    if (instructorId) params.set('instructorId', String(instructorId));
    if (instructorName) params.set('instructorName', instructorName);
    return params.toString();
  };
  const openAttendanceJava = (
    batchIds: number[],
    templateSlotId: number,
    instructorId?: number,
    instructorName?: string,
  ) => {
    window.open(
      `/attendance/batches/${batchIds.join(',')}?${buildAttendanceQuery(templateSlotId, instructorId, instructorName)}`,
      '_blank',
    );
  };
  const openAttendanceNode = (
    batchIds: number[],
    templateSlotId: number,
    instructorId?: number,
    instructorName?: string,
  ) => {
    window.open(
      `/attendance-node/batches/${batchIds.join(',')}?${buildAttendanceQuery(templateSlotId, instructorId, instructorName)}`,
      '_blank',
    );
  };

  // Decorate each slot with its active substitution (if any) for that date.
  // The substitution table is keyed by (templateId, dateRange) so we match
  // each slot's date against each substitution's StartDate..EndDate window.
  const decoratedSlots = useMemo<ActivitySlot[]>(() => {
    const baseSlots = template?.activitySlotTemplates || [];
    if (!substitutions || substitutions.length === 0) return baseSlots;
    const byTemplate = new Map<number, Substitution[]>();
    substitutions.forEach((s) => {
      const list = byTemplate.get(s.activitySlotTemplateId) || [];
      list.push(s);
      byTemplate.set(s.activitySlotTemplateId, list);
    });
    return baseSlots.map((slot) => {
      const candidates = byTemplate.get(slot.id);
      if (!candidates || !slot.date) return slot;
      const slotDate = new Date(slot.date).getTime();
      // Prefer APPROVED match, fall back to REQUESTED
      const matchInRange = (s: Substitution) => {
        const a = new Date(s.startDate).getTime();
        const b = new Date(s.endDate).getTime();
        return slotDate >= a && slotDate <= b;
      };
      const approved = candidates.find((s) => s.status === 'APPROVED' && matchInRange(s));
      const requested = candidates.find((s) => s.status === 'REQUESTED' && matchInRange(s));
      const active = approved || requested;
      if (!active) return slot;
      return {
        ...slot,
        substitution: {
          id: active.id,
          status: active.status,
          substituteInstructorId: active.substituteInstructorId ?? null,
          substituteInstructorName: active.substituteInstructorName ?? null,
          substituteTopicId: active.substituteTopicId ?? null,
          substituteTopicName: active.substituteTopicName ?? null,
          substituteAgenda: active.substituteAgenda ?? null,
          reason: active.reason ?? null,
        },
      };
    });
  }, [template, substitutions]);
  const [slotFormData, setSlotFormData] = useState({
    date: '',
    startTime: '09:00',
    endTime: '10:00',
    instructorId: '',
    instructorName: '',
    topicMasterId: '',
    topicName: '',
    agenda: '',
    onlineMeeting: false,
    zoomMeetingId: '',
    onlineMeetingType: '',
    batchIds: [] as number[],
    repeatWeekly: false,
    roomId: '',
    roomName: '',
  });
  const [originalSlotFormData, setOriginalSlotFormData] = useState<typeof slotFormData | null>(null);
  const [instructorSearch, setInstructorSearch] = useState('');
  const [topicSearch, setTopicSearch] = useState('');
  const [instructorOptions, setInstructorOptions] = useState<any[]>([]);
  const [topicOptions, setTopicOptions] = useState<any[]>([]);
  const [showInstructorDropdown, setShowInstructorDropdown] = useState(false);
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  const [availableBatches, setAvailableBatches] = useState<any[]>([]);

  // Loading states for autocomplete
  const [instructorSearchLoading, setInstructorSearchLoading] = useState(false);
  const [topicSearchLoading, setTopicSearchLoading] = useState(false);

  // Linked selection: filtered options when one field is selected
  const [filteredInstructors, setFilteredInstructors] = useState<any[]>([]);
  const [filteredTopics, setFilteredTopics] = useState<any[]>([]);

  // Track which field was selected first (for dynamic field ordering)
  const [firstSelected, setFirstSelected] = useState<'instructor' | 'topic' | null>(null);

  // Batch management state
  const [batchSearch, setBatchSearch] = useState('');
  const [batchOptions, setBatchOptions] = useState<any[]>([]);
  const [showBatchDropdown, setShowBatchDropdown] = useState(false);
  const [isManagingBatches, setIsManagingBatches] = useState(false);

  // Room options for slot edit modal
  const [roomOptions, setRoomOptions] = useState<any[]>([]);

  // Meeting type options for online meetings — fetched from
  // study_resource_sub_type_master rows with StudyResourceTypeId = 30
  // (online meeting providers). Each row's Id is what gets saved into the
  // slot's OnlineMeetingType column; backend dispatches by reading config.op
  // from the matching row.
  const [meetingTypeOptions, setMeetingTypeOptions] = useState<any[]>([]);

  // Collapsible sections
  const [showTemplateInfo, setShowTemplateInfo] = useState(false);
  const [showBatchSection, setShowBatchSection] = useState(false);
  const [showRollback, setShowRollback] = useState(false);

  // Tab navigation state
  const [activeTab, setActiveTab] = useState<'pattern' | 'slots' | 'visual' | 'macroplan' | 'conducted'>('visual');

  // Macro Plan tab state — mirrors the execute board's macro planning page
  const [macroPlanConfigs, setMacroPlanConfigs] = useState<MacroPlanConfig[]>([]);
  const [macroPlanLoaded, setMacroPlanLoaded] = useState(false);

  // Excel import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState<'slots' | 'pattern'>('slots');

  // System config
  const [isProgramBased, setIsProgramBased] = useState<boolean | null>(null);

  // Permissions
  const [permissions, setPermissions] = useState<TimetablePermissions>({
    canTakeAttendance: true,
    canEditTemplate: true,
    canDeleteTemplate: true,
    canCreateTemplate: true,
  });

  // Conflict groups for Visual Overview filter
  const [conflictGroups, setConflictGroups] = useState<ConflictGroup[]>([]);

  // Filtered slots for Manage Slots tab
  const [filteredSlotsForList, setFilteredSlotsForList] = useState<ActivitySlot[]>([]);

  // Swap slots state
  const [swapSlotIds, setSwapSlotIds] = useState<[number, number] | null>(null);
  const [swapIncludeRoom, setSwapIncludeRoom] = useState(false);

  // Extend timetable state
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [newEndDate, setNewEndDate] = useState('');
  const [generateOnExtend, setGenerateOnExtend] = useState(true);

  // Modal states
  const [confirmModal, setConfirmModal] = useState<{
    title?: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);
  const [alertModal, setAlertModal] = useState<{
    title?: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    actionUrl?: string;
    actionLabel?: string;
  } | null>(null);

  useEffect(() => {
    fetchTemplateDetail();
    ApiService.getScheduleSystemConfig()
      .then(response => {
        if (response.data.success) {
          setIsProgramBased(response.data.data.isProgramBased);
        }
      })
      .catch(err => console.error('Failed to fetch system config:', err));
  }, [id]);

  const fetchTemplateDetail = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await ApiService.getTemplateDetail(parseInt(id));
      if (response.data.success) {
        const templateData = response.data.data;
        setTemplate(templateData);
        setEditedName(templateData.name);
        setEditedRemarks(templateData.remarks || '');
        // Set available batches for activity slot selection
        setAvailableBatches(templateData.batchMappings || []);
        // Set permissions from API response
        if (response.data.permissions) {
          setPermissions(response.data.permissions);
        }
        // Fetch conflict groups for the source board (if template was created from a board)
        fetchConflictGroups(templateData.config);
        // Fetch substitutions for this template's slots
        const slotIds: number[] = (templateData.activitySlotTemplates || [])
          .map((s: any) => s.id)
          .filter((n: any) => typeof n === 'number');
        if (slotIds.length > 0) {
          try {
            const subs = await listSubstitutions({
              templateIds: slotIds,
              status: ['REQUESTED', 'APPROVED'],
            });
            setSubstitutions(subs);
          } catch (subErr) {
            console.error('Failed to fetch substitutions:', subErr);
            setSubstitutions([]);
          }
        } else {
          setSubstitutions([]);
        }
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      setAlertModal({ message: 'Failed to load template details', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchConflictGroups = async (config: string | null) => {
    try {
      let boardId: number | undefined;
      if (config) {
        try {
          const parsed = JSON.parse(config);
          if (parsed.sourceBoardId) boardId = parsed.sourceBoardId;
        } catch { /* config may not be valid JSON */ }
      }
      const response: any = await ConflictGroupService.listGroups({
        boardId,
        includeMembers: true,
      });
      if (response.data.success) {
        setConflictGroups(response.data.groups || []);
      }
    } catch (error) {
      console.error('Failed to fetch conflict groups:', error);
    }
  };

  const loadMacroPlanConfigs = async () => {
    if (!template) return;
    try {
      const data = await MacroPlanningService.getConfigs(0, template.id);
      setMacroPlanConfigs(data);
      setMacroPlanLoaded(true);
    } catch (error) {
      console.error('Failed to load macro plan configs:', error);
      setMacroPlanLoaded(true);
    }
  };

  const handleSaveMacroPlanConfig = async (config: MacroPlanConfig) => {
    try {
      await MacroPlanningService.saveConfig(config);
      await loadMacroPlanConfigs();
    } catch (error: any) {
      console.error('Failed to save macro plan config:', error);
      window.alert(`Failed to save: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    }
  };

  useEffect(() => {
    if (activeTab === 'macroplan' && template && !macroPlanLoaded) {
      loadMacroPlanConfigs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, template?.id]);

  const handleSave = async () => {
    if (!template) return;

    try {
      const updateData: any = {};
      if (editedName !== template.name) updateData.name = editedName;
      if (editedRemarks !== template.remarks) updateData.remarks = editedRemarks;

      if (Object.keys(updateData).length > 0) {
        const response = await ApiService.updateTemplate(template.id, updateData);
        if (response.data.success) {
          setAlertModal({ message: 'Template updated successfully', type: 'success' });
          fetchTemplateDetail();
        }
      }
    } catch (error: any) {
      console.error('Error updating template:', error);
      setAlertModal({ message: error.response?.data?.error || 'Failed to update template', type: 'error' });
    }
  };

  // ----- Approval workflow (PSB B.1.a) ----------------------------------

  const slotConfirmationCounts = useMemo(() => {
    const all = template?.activitySlotTemplates || [];
    const confirmed = all.filter(
      (s) => s.confirmedByUserId !== null && s.confirmedByUserId !== undefined,
    ).length;
    return { confirmed, total: all.length };
  }, [template]);

  const handlePublishTemplate = () => {
    if (!template) return;
    const { confirmed, total } = slotConfirmationCounts;
    const unconfirmed = total - confirmed;
    const warning =
      unconfirmed > 0
        ? `Note: ${unconfirmed} of ${total} slot${total !== 1 ? 's are' : ' is'} not yet confirmed by the instructor and will remain hidden from students until confirmed.\n\n`
        : "";
    setConfirmModal({
      title: "Publish Timetable",
      message: `${warning}Publish this timetable so confirmed slots become visible to students?`,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await ApiService.publishTemplate(template.id);
          if (response.data.success) {
            setAlertModal({
              message: "Timetable published. Confirmed slots are now visible to students.",
              type: "success",
            });
            fetchTemplateDetail();
          }
        } catch (error: any) {
          console.error("Error publishing timetable:", error);
          setAlertModal({
            message:
              error.response?.data?.error || "Failed to publish timetable",
            type: "error",
          });
        }
      },
    });
  };

  const handleUnpublishTemplate = () => {
    if (!template) return;
    setConfirmModal({
      title: "Unpublish Timetable",
      message:
        "Pull this timetable back from student view? Students will stop seeing all slots in this timetable immediately.",
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await ApiService.unpublishTemplate(template.id);
          if (response.data.success) {
            setAlertModal({
              message: "Timetable unpublished.",
              type: "success",
            });
            fetchTemplateDetail();
          }
        } catch (error: any) {
          console.error("Error unpublishing timetable:", error);
          setAlertModal({
            message:
              error.response?.data?.error || "Failed to unpublish timetable",
            type: "error",
          });
        }
      },
    });
  };

  const handleConfirmSlot = async (slotId: number) => {
    if (!template) return;
    try {
      const response = await ApiService.confirmActivitySlot(template.id, slotId);
      if (response.data.success) {
        fetchTemplateDetail();
      }
    } catch (error: any) {
      console.error("Error confirming slot:", error);
      setAlertModal({
        message: error.response?.data?.error || "Failed to confirm slot",
        type: "error",
      });
    }
  };

  const handleUnconfirmSlot = async (slotId: number) => {
    if (!template) return;
    try {
      const response = await ApiService.unconfirmActivitySlot(template.id, slotId);
      if (response.data.success) {
        fetchTemplateDetail();
      }
    } catch (error: any) {
      console.error("Error unconfirming slot:", error);
      setAlertModal({
        message: error.response?.data?.error || "Failed to revoke confirmation",
        type: "error",
      });
    }
  };

  const handleDeleteSlot = (slotId: number) => {
    if (!template) return;

    setConfirmModal({
      title: 'Delete Slot',
      message: 'Are you sure you want to delete this activity slot?',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await ApiService.deleteActivitySlotTemplate(template.id, slotId);
          if (response.data.success) {
            setAlertModal({ message: 'Activity slot deleted successfully', type: 'success' });
            fetchTemplateDetail();
          }
        } catch (error: any) {
          console.error('Error deleting slot:', error);
          setAlertModal({ message: error.response?.data?.error || 'Failed to delete activity slot', type: 'error' });
        }
      },
    });
  };

  const handleBulkDeleteSlots = (slotIds: number[]) => {
    if (!template || slotIds.length === 0) return;

    setConfirmModal({
      title: 'Delete Selected Slots',
      message: `Are you sure you want to delete ${slotIds.length} activity slot${slotIds.length !== 1 ? 's' : ''}? This cannot be undone.`,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await ApiService.bulkDeleteActivitySlotTemplates(template.id, slotIds);
          if (response.data.success) {
            setAlertModal({
              message: response.data.message || `${response.data.deletedCount} slot(s) deleted successfully`,
              type: 'success',
            });
            fetchTemplateDetail();
          }
        } catch (error: any) {
          console.error('Error bulk deleting slots:', error);
          setAlertModal({
            message: error.response?.data?.error || 'Failed to delete activity slots',
            type: 'error',
          });
        }
      },
    });
  };

  const handleSwapRequest = (slotIdA: number, slotIdB: number) => {
    setSwapSlotIds([slotIdA, slotIdB]);
    setSwapIncludeRoom(false);
  };

  const handleSwapConfirm = async () => {
    if (!template || !swapSlotIds) return;
    const [idA, idB] = swapSlotIds;
    setSwapSlotIds(null);
    try {
      const response = await ApiService.swapActivitySlotTemplates(template.id, idA, idB, 'content', swapIncludeRoom);
      if (response.data.success) {
        setAlertModal({ message: response.data.message || 'Slots swapped successfully', type: 'success' });
        fetchTemplateDetail();
      }
    } catch (error: any) {
      console.error('Error swapping slots:', error);
      const data = error.response?.data;
      // Show conflict details if returned
      const message = data?.conflicts?.length
        ? `Swap blocked:\n${data.conflicts.join('\n')}`
        : data?.error || 'Failed to swap slots';
      setAlertModal({ message, type: 'error' });
    }
  };

  const handleAddSlot = (date?: string) => {
    setEditingSlot(null);
    setOriginalSlotFormData(null);
    setSlotFormData({
      date: date || '',
      startTime: '09:00',
      endTime: '10:00',
      instructorId: '',
      instructorName: '',
      topicMasterId: '',
      topicName: '',
      agenda: '',
      onlineMeeting: false,
      zoomMeetingId: '',
      onlineMeetingType: '',
      batchIds: [],
      repeatWeekly: false,
      roomId: '',
      roomName: '',
    });
    setInstructorSearch('');
    setTopicSearch('');
    setInstructorOptions([]);
    setTopicOptions([]);
    setFilteredInstructors([]);
    setFilteredTopics([]);
    setFirstSelected(null);
    loadRoomOptions();
    loadMeetingTypeOptions();
    setShowSlotModal(true);
  };

  const loadMeetingTypeOptions = async () => {
    if (meetingTypeOptions.length > 0) return;
    try {
      const response = await ApiService.getStudyResourceSubTypes(30); // TypeId=30 for online meeting platforms
      if (response.data) {
        setMeetingTypeOptions(response.data || []);
      }
    } catch (error) {
      console.error('Error loading meeting types:', error);
    }
  };

  const loadRoomOptions = async () => {
    if (roomOptions.length > 0) return;
    try {
      const response = await ApiService.getClassrooms({ isActive: 1 });
      if (response.data?.success) {
        setRoomOptions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
    }
  };

  const handleEditSlot = async (slot: ActivitySlot) => {
    setEditingSlot(slot);
    const initialFormData = {
      date: slot.date,
      startTime: slot.startTime.substring(0, 5), // HH:mm format
      endTime: slot.endTime.substring(0, 5),
      instructorId: slot.instructorId?.toString() || '',
      instructorName: slot.instructorName || '',
      topicMasterId: slot.topicMasterId?.toString() || '',
      topicName: slot.topicName || '',
      agenda: slot.agenda || '',
      onlineMeeting: !!slot.onlineMeetingType,
      zoomMeetingId: slot.zoomMeetingId || '',
      onlineMeetingType: slot.onlineMeetingType?.toString() || '',
      batchIds: slot.batchIds || [],
      repeatWeekly: false,
      roomId: slot.roomId?.toString() || '',
      roomName: slot.roomName || '',
    };
    setSlotFormData(initialFormData);
    setOriginalSlotFormData(initialFormData);
    setInstructorSearch(slot.instructorName || '');
    setTopicSearch(slot.topicName || '');
    setInstructorOptions([]);
    setTopicOptions([]);
    setFilteredInstructors([]);
    setFilteredTopics([]);
    loadRoomOptions();
    loadMeetingTypeOptions();
    // When editing, default to instructor-first order; only set topic-first if only topic is filled
    if (slot.topicMasterId && !slot.instructorId) {
      setFirstSelected('topic');
    } else {
      setFirstSelected(null);
    }
    setShowSlotModal(true);

    // Load filtered options based on existing selections
    if (slot.instructorId && template) {
      try {
        const response = await ApiService.getTopicsForInstructor(template.id, slot.instructorId, slot.batchIds || []);
        if (response.data.success) setFilteredTopics(response.data.data || []);
      } catch (error) {
        console.error('Error fetching topics for instructor:', error);
      }
    }
    if (slot.topicMasterId && template) {
      try {
        const batchIdsForFilter = slot.batchIds || [];
        const response = await ApiService.getInstructorsForTopic(template.id, slot.topicMasterId, batchIdsForFilter);
        if (response.data.success) setFilteredInstructors(response.data.data || []);
      } catch (error) {
        console.error('Error fetching instructors for topic:', error);
      }
    }
  };

  const handleInstructorSearch = async (query: string) => {
    setInstructorSearch(query);
    if (query.length >= 2 && template) {
      setInstructorSearchLoading(true);
      setShowInstructorDropdown(true);
      try {
        // Use course-aware search filtered by selected batches
        const response = await ApiService.searchInstructorsByTemplate(template.id, query, slotFormData.batchIds);
        if (response.data.success) {
          setInstructorOptions(response.data.data || []);
        }
      } catch (error) {
        console.error('Error searching instructors:', error);
        setInstructorOptions([]);
      } finally {
        setInstructorSearchLoading(false);
      }
    } else {
      setInstructorOptions([]);
      setShowInstructorDropdown(false);
    }
  };

  const handleTopicSearch = async (query: string) => {
    setTopicSearch(query);
    if (query.length >= 2 && template) {
      setTopicSearchLoading(true);
      setShowTopicDropdown(true);
      try {
        // Search topics scoped to the selected batches.
        const response = await ApiService.searchTopicsByTemplate(template.id, query, slotFormData.batchIds);
        if (response.data.success) {
          setTopicOptions(response.data.data || []);
        }
      } catch (error) {
        console.error('Error searching topics:', error);
        setTopicOptions([]);
      } finally {
        setTopicSearchLoading(false);
      }
    } else {
      setTopicOptions([]);
      setShowTopicDropdown(false);
    }
  };

  const selectInstructor = async (instructor: any) => {
    const name = instructor.name || `${instructor.firstName} ${instructor.lastName}`;
    setSlotFormData(prev => ({
      ...prev,
      instructorId: instructor.id.toString(),
      instructorName: name
    }));
    setInstructorSearch(name);
    setShowInstructorDropdown(false);

    // Track which field was selected first
    if (!firstSelected) setFirstSelected('instructor');

    // Fetch filtered topics for this instructor
    if (template) {
      try {
        const response = await ApiService.getTopicsForInstructor(template.id, instructor.id, slotFormData.batchIds);
        if (response.data.success) {
          setFilteredTopics(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching topics for instructor:', error);
      }
    }
  };

  const clearInstructor = () => {
    setSlotFormData(prev => ({
      ...prev,
      instructorId: '',
      instructorName: ''
    }));
    setInstructorSearch('');
    setFilteredTopics([]);
    if (firstSelected === 'instructor') setFirstSelected(null);
  };

  const selectTopic = async (topic: any) => {
    setSlotFormData(prev => ({
      ...prev,
      topicMasterId: topic.id.toString(),
      topicName: topic.name
    }));
    setTopicSearch(topic.name);
    setShowTopicDropdown(false);

    // Track which field was selected first
    if (!firstSelected) setFirstSelected('topic');

    // Fetch filtered instructors for this topic (filtered by selected batches)
    if (template) {
      try {
        const response = await ApiService.getInstructorsForTopic(template.id, topic.id, slotFormData.batchIds);
        if (response.data.success) {
          setFilteredInstructors(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching instructors for topic:', error);
      }
    }
  };

  const clearTopic = () => {
    setSlotFormData(prev => ({
      ...prev,
      topicMasterId: '',
      topicName: ''
    }));
    setTopicSearch('');
    setFilteredInstructors([]);
    if (firstSelected === 'topic') setFirstSelected(null);
  };

  // Toggling a batch resets Instructor/Topic — those options are batch-scoped, so the next
  // pick must always reflect the currently-selected batches.
  const handleSlotBatchToggle = (batchId: number, checked: boolean) => {
    const newBatchIds = checked
      ? [...slotFormData.batchIds, batchId]
      : slotFormData.batchIds.filter(id => id !== batchId);

    setSlotFormData(prev => ({
      ...prev,
      batchIds: newBatchIds,
      instructorId: '',
      instructorName: '',
      topicMasterId: '',
      topicName: '',
    }));
    setInstructorSearch('');
    setTopicSearch('');
    setInstructorOptions([]);
    setTopicOptions([]);
    setShowInstructorDropdown(false);
    setShowTopicDropdown(false);
    setFilteredInstructors([]);
    setFilteredTopics([]);
    setFirstSelected(null);
  };

  // Batch management functions
  const handleBatchSearch = async (query: string) => {
    setBatchSearch(query);
    if (query.length >= 2) {
      try {
        const response = await ApiService.searchBatches(query, template?.programId);
        if (response.data.success) {
          setBatchOptions(response.data.data || []);
          setShowBatchDropdown(true);
        }
      } catch (error) {
        console.error('Error searching batches:', error);
      }
    } else {
      setBatchOptions([]);
      setShowBatchDropdown(false);
    }
  };

  const handleAddBatch = async (batch: any) => {
    if (!template) return;

    const currentBatchIds = template.batchMappings?.map((b: any) => b.batchId) || [];
    if (currentBatchIds.includes(batch.id)) {
      setAlertModal({ message: 'This batch is already associated with the template', type: 'info' });
      return;
    }

    try {
      const updatedBatchIds = [...currentBatchIds, batch.id];
      const response = await ApiService.updateTemplateBatches(template.id, updatedBatchIds);

      if (response.data.success) {
        setAlertModal({ message: 'Batch added successfully', type: 'success' });
        setBatchSearch('');
        setBatchOptions([]);
        setShowBatchDropdown(false);
        fetchTemplateDetail();
      }
    } catch (error: any) {
      console.error('Error adding batch:', error);
      setAlertModal({ message: error.response?.data?.message || 'Failed to add batch', type: 'error' });
    }
  };

  const handleRemoveBatch = (batchId: number) => {
    if (!template) return;

    setConfirmModal({
      title: 'Remove Batch',
      message: 'Are you sure you want to remove this batch from the template?',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const currentBatchIds = template.batchMappings?.map((b: any) => b.batchId) || [];
          const updatedBatchIds = currentBatchIds.filter((id: number) => id !== batchId);
          const response = await ApiService.updateTemplateBatches(template.id, updatedBatchIds);

          if (response.data.success) {
            setAlertModal({ message: 'Batch removed successfully', type: 'success' });
            fetchTemplateDetail();
          }
        } catch (error: any) {
          console.error('Error removing batch:', error);
          setAlertModal({ message: error.response?.data?.message || 'Failed to remove batch', type: 'error' });
        }
      },
    });
  };

  const handleSlotFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSlotFormData(prev => ({ ...prev, [name]: value }));
  };

  const isSlotFormDirty = useMemo(() => {
    if (!originalSlotFormData) return true;
    const a = originalSlotFormData;
    const b = slotFormData;
    if (a.date !== b.date) return true;
    if (a.startTime !== b.startTime) return true;
    if (a.endTime !== b.endTime) return true;
    if (a.instructorId !== b.instructorId) return true;
    if (a.topicMasterId !== b.topicMasterId) return true;
    if ((a.agenda || '') !== (b.agenda || '')) return true;
    if ((a.zoomMeetingId || '') !== (b.zoomMeetingId || '')) return true;
    if ((a.onlineMeetingType || '') !== (b.onlineMeetingType || '')) return true;
    if ((a.roomId || '') !== (b.roomId || '')) return true;
    const aBatches = [...(a.batchIds || [])].sort();
    const bBatches = [...(b.batchIds || [])].sort();
    if (aBatches.length !== bBatches.length) return true;
    for (let i = 0; i < aBatches.length; i++) {
      if (aBatches[i] !== bBatches[i]) return true;
    }
    return false;
  }, [originalSlotFormData, slotFormData]);

  // Guards against double-submission — the Save button doesn't naturally
  // disable during an in-flight request, so a fast double-click was creating
  // two identical POSTs and therefore two slot rows.
  const [isSavingSlot, setIsSavingSlot] = useState(false);

  const handleSaveSlot = async () => {
    if (!template) return;
    if (editingSlot && !isSlotFormDirty) return;
    if (isSavingSlot) return;
    setIsSavingSlot(true);

    try {
      const payload = {
        date: slotFormData.date,
        startTime: slotFormData.startTime + ':00',
        endTime: slotFormData.endTime + ':00',
        instructorId: slotFormData.instructorId ? parseInt(slotFormData.instructorId) : undefined,
        topicMasterId: slotFormData.topicMasterId ? parseInt(slotFormData.topicMasterId) : undefined,
        agenda: slotFormData.agenda || undefined,
        batchIds: slotFormData.batchIds,
        isOnlineMeeting: slotFormData.onlineMeeting,
        zoomMeetingId: slotFormData.zoomMeetingId || undefined,
        onlineMeetingType: slotFormData.onlineMeetingType ? parseInt(slotFormData.onlineMeetingType) : undefined,
        roomId: slotFormData.roomId ? parseInt(slotFormData.roomId) : null,
      };

      // Build the alert payload for a save response, including any meeting-creation
      // warnings the backend surfaced via response.data.meetingErrors[]. The slot
      // itself is already persisted at this point — meetingErrors only indicate the
      // online-meeting side (Zoom/Teams) needs attention.
      const buildAlertFromResponse = (
        responseData: any,
        defaultSuccessMessage: string,
        verb: 'created' | 'updated',
        meetingType?: number
      ): {
        message: string;
        type: 'success' | 'warning';
        actionUrl?: string;
        actionLabel?: string;
      } => {
        const meetingErrors: Array<{ error: string; authUrl?: string }> = responseData.meetingErrors || [];
        const baseSuccessMessage = responseData.message || defaultSuccessMessage;
        if (meetingErrors.length === 0) {
          return { message: baseSuccessMessage, type: 'success' };
        }

        // Determine provider name based on meeting type
        let providerName = 'the meeting provider';
        if (meetingType === ONLINE_MEETING_ZOOM) {
          providerName = 'Zoom';
        } else if (meetingType === ONLINE_MEETING_TEAMS) {
          providerName = 'Teams';
        } else if (meetingType === ONLINE_MEETING_WEBINAR) {
          providerName = 'Zoom Webinar';
        }

        const first = meetingErrors[0];
        const errText = first.error || 'Unknown error';
        const looksLikeAuth = first.authUrl || /authoriz|token|invalid_grant/i.test(errText);
        const messageLines = [
          `Activity slot ${verb}, but online meeting creation failed.`,
          '',
          `Reason: ${errText}`,
        ];
        if (looksLikeAuth && first.authUrl) {
          messageLines.push('', `Please re-authorize ${providerName} and edit the slot again to create the meeting.`);
        }
        if (meetingErrors.length > 1) {
          messageLines.push('', `(${meetingErrors.length - 1} additional meeting errors on other slots — same cause.)`);
        }
        return {
          message: messageLines.join('\n'),
          type: 'warning',
          ...(first.authUrl ? { actionUrl: first.authUrl, actionLabel: `Authorize ${providerName}` } : {}),
        };
      };

      if (editingSlot) {
        // Update existing slot
        const updatePayload = {
          ...payload,
          ...(slotFormData.repeatWeekly ? { applyToFutureSlots: true } : {}),
        };
        const response = await ApiService.updateActivitySlotTemplate(template.id, editingSlot.id, updatePayload);
        if (response.data.success) {
          const defaultMsg = response.data.updatedCount > 1
            ? response.data.message
            : 'Activity slot updated successfully';
          setAlertModal(buildAlertFromResponse(response.data, defaultMsg, 'updated', slotFormData.onlineMeetingType ? parseInt(slotFormData.onlineMeetingType) : undefined));
          setShowSlotModal(false);
          fetchTemplateDetail();
        }
      } else {
        // Create new slot(s)
        const createPayload = {
          ...payload,
          repeatWeekly: slotFormData.repeatWeekly || undefined,
        };
        const response = await ApiService.addActivitySlotTemplate(template.id, createPayload);
        if (response.data.success) {
          const defaultMsg = response.data.data?.count != null
            ? response.data.message
            : 'Activity slot added successfully';
          setAlertModal(buildAlertFromResponse(response.data, defaultMsg, 'created', slotFormData.onlineMeetingType ? parseInt(slotFormData.onlineMeetingType) : undefined));
          setShowSlotModal(false);
          fetchTemplateDetail();
        }
      }
    } catch (error: any) {
      console.error('Error saving slot:', error);
      const data = error.response?.data;
      const message = data?.conflicts?.length
        ? data.conflicts.join('\n')
        : data?.error || 'Failed to save activity slot';
      setAlertModal({ message, type: 'error' });
    } finally {
      setIsSavingSlot(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const handleGenerate = () => {
    setActiveTab('slots'); // Switch to manage slots tab
    fetchTemplateDetail(); // Refresh to show generated slots
  };

  const openExtendModal = () => {
    if (!template) return;
    setNewEndDate('');
    setGenerateOnExtend(true);
    setShowExtendModal(true);
  };

  const handleExtend = async () => {
    if (!template) return;
    if (!newEndDate) {
      setAlertModal({ message: 'Please select a new end date.', type: 'error' });
      return;
    }
    if (newEndDate <= template.endDate) {
      setAlertModal({ message: 'New end date must be after the current end date.', type: 'error' });
      return;
    }

    setShowExtendModal(false);
    try {
      // Step 1: Update the template end date
      const updateResponse = await ApiService.updateTemplate(template.id, { endDate: newEndDate });
      if (!updateResponse.data.success) {
        setAlertModal({ message: 'Failed to update template end date.', type: 'error' });
        return;
      }

      // Step 2: Generate slots for the extended period if requested
      if (generateOnExtend) {
        const nextDay = new Date(template.endDate);
        nextDay.setDate(nextDay.getDate() + 1);
        const generateStartDate = nextDay.toISOString().split('T')[0];

        const genResponse = await ApiService.generateSlotsFromPattern(template.id, generateStartDate, newEndDate);
        if (genResponse.data.success) {
          const count = genResponse.data.data?.count || 0;
          setAlertModal({
            message: count > 0
              ? `Timetable extended to ${formatDate(newEndDate)}. Generated ${count} new slots.`
              : `Timetable extended to ${formatDate(newEndDate)}. No new slots were generated (pattern may be empty or slots already exist).`,
            type: count > 0 ? 'success' : 'info',
          });
        }
      } else {
        setAlertModal({ message: `Timetable extended to ${formatDate(newEndDate)}.`, type: 'success' });
      }

      fetchTemplateDetail();
    } catch (error: any) {
      console.error('Error extending timetable:', error);
      setAlertModal({ message: error.response?.data?.error || 'Failed to extend timetable.', type: 'error' });
    }
  };

  const handleSlotMoved = () => {
    fetchTemplateDetail();
  };

  if (loading) {
    return <div className="template-detail-page"><div className="loading">Loading template...</div></div>;
  }

  if (!template) {
    return <div className="template-detail-page"><div className="error">Template not found</div></div>;
  }

  return (
    <div className="template-detail-page">
      <div className="detail-header">
        <div className="header-title-block">
          <h1>{isEditMode ? 'Edit Template' : 'View Template'}</h1>
          <div className="header-status-row">
            <span
              className={`publish-badge publish-${(template.publishStatus || 'Draft').toLowerCase()}`}
              title={
                template.publishStatus === 'Published' && template.publishedAt
                  ? `Published ${new Date(template.publishedAt).toLocaleString()}`
                  : undefined
              }
            >
              {template.publishStatus || 'Draft'}
            </span>
            <span className="confirmation-summary">
              {slotConfirmationCounts.confirmed}/{slotConfirmationCounts.total} slots confirmed
            </span>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-back" onClick={() => history.push('/schedules-templates')}>
            ← Back to List
          </button>
          {permissions.canEditTemplate && (
            <button className="btn-extend" onClick={openExtendModal}>
              Extend Timetable
            </button>
          )}
          {!isEditMode && permissions.canEditTemplate && (
            <button
              className="btn-schedule"
              onClick={() => history.push(`/template-scheduling-board/${id}`)}
              title="Open in Scheduling Board"
            >
              Schedule
            </button>
          )}
          {!isEditMode && permissions.canEditTemplate && (
            <button className="btn-save" onClick={() => history.push(`/schedules-templates/${id}?edit=true`)}>
              Edit Template
            </button>
          )}
          {!isEditMode && permissions.canEditTemplate && template.publishStatus !== 'Published' && (
            <button className="btn-publish" onClick={handlePublishTemplate} title="Make confirmed slots visible to students">
              Publish to Students
            </button>
          )}
          {!isEditMode && permissions.canEditTemplate && template.publishStatus === 'Published' && (
            <button className="btn-unpublish" onClick={handleUnpublishTemplate} title="Pull timetable back from student view">
              Unpublish
            </button>
          )}
          {isEditMode && (
            <button className="btn-back" onClick={() => history.push(`/schedules-templates/${id}`)}>
              Cancel Editing
            </button>
          )}
        </div>
      </div>

      {/* Metadata Section — collapsible */}
      <div className="metadata-section collapsible-section">
        <div className="collapsible-header" onClick={() => setShowTemplateInfo(!showTemplateInfo)}>
          <h2>Template Information</h2>
          <div className="collapsible-summary">
            <span className="summary-chip">{template.name}</span>
            <span className="summary-chip">{formatDate(template.startDate)} — {formatDate(template.endDate)}</span>
            <span className={`status-badge ${template.activeStatus === 1 ? 'active' : 'inactive'}`}>
              {template.activeStatus === 1 ? 'Active' : 'Inactive'}
            </span>
          </div>
          <span className={`collapsible-arrow ${showTemplateInfo ? 'open' : ''}`}>&#9662;</span>
        </div>
        <div className={`collapsible-body ${showTemplateInfo ? 'open' : ''}`}>
        <div className="metadata-grid">
          <div className="field">
            <label>Template Name</label>
            {isEditMode ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
              />
            ) : (
              <div className="value">{template.name}</div>
            )}
          </div>

          <div className="field">
            <label>Start Date</label>
            <div className="value">{formatDate(template.startDate)}</div>
          </div>

          <div className="field">
            <label>End Date</label>
            <div className="value">{formatDate(template.endDate)}</div>
          </div>

          <div className="field">
            <label>Year</label>
            <div className="value">{template.yearName || '-'}</div>
          </div>

          {isProgramBased !== false && (
            <div className="field">
              <label>Program</label>
              <div className="value">{template.programName || '-'}</div>
            </div>
          )}

          {isProgramBased !== false && (
            <div className="field">
              <label>Term</label>
              <div className="value">{template.termName || '-'}</div>
            </div>
          )}

          {isProgramBased !== true && (
            <div className="field">
              <label>Course</label>
              <div className="value">{template.courseName || '-'}</div>
            </div>
          )}

          <div className="field">
            <label>Status</label>
            <div className="value">
              <span className={`status-badge ${template.activeStatus === 1 ? 'active' : 'inactive'}`}>
                {template.activeStatus === 1 ? 'Active' : 'Inactive'}
              </span>
              {template.lockStatus === 1 && (
                <span className="status-badge" style={{marginLeft: '8px', background: '#ffe082', color: '#f57f17'}}>
                  🔒 Locked
                </span>
              )}
            </div>
          </div>

          <div className="field" style={{gridColumn: '1 / -1'}}>
            <label>Remarks</label>
            {isEditMode ? (
              <textarea
                value={editedRemarks}
                onChange={(e) => setEditedRemarks(e.target.value)}
                rows={3}
                style={{width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid var(--border-default)'}}
              />
            ) : (
              <div className="value">{template.remarks || '-'}</div>
            )}
          </div>
          {isEditMode && (
            <div style={{gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', marginTop: '4px'}}>
              <button className="btn-save" onClick={handleSave}>
                Save Name & Remarks
              </button>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Rollback / History Section — collapsible */}
      <div className="rollback-section collapsible-section">
        <div className="collapsible-header" onClick={() => setShowRollback(!showRollback)}>
          <h2>History &amp; Rollback</h2>
          <div className="collapsible-summary">
            <span className="summary-chip">Undo · Snapshots · Versions</span>
          </div>
          <span className={`collapsible-arrow ${showRollback ? 'open' : ''}`}>&#9662;</span>
        </div>
        <div className={`collapsible-body ${showRollback ? 'open' : ''}`}>
          {showRollback && (
            <RollbackPanel
              templateId={template.id}
              onChange={() => fetchTemplateDetail()}
            />
          )}
        </div>
      </div>

      {/* Batch Management Section — collapsible */}
      <div className="batch-management-section collapsible-section">
        <div className="collapsible-header" onClick={() => setShowBatchSection(!showBatchSection)}>
          <h2>Associated Batches ({template.batchMappings?.length || 0})</h2>
          <div className="collapsible-summary">
            {template.batchMappings && template.batchMappings.length > 0 ? (
              template.batchMappings.slice(0, 4).map((b: any) => (
                <span key={b.batchId} className="summary-chip">{b.batchCode || b.batchName}</span>
              ))
            ) : (
              <span className="summary-chip muted">None</span>
            )}
            {(template.batchMappings?.length || 0) > 4 && (
              <span className="summary-chip muted">+{template.batchMappings!.length - 4} more</span>
            )}
          </div>
          <span className={`collapsible-arrow ${showBatchSection ? 'open' : ''}`}>&#9662;</span>
        </div>
        <div className={`collapsible-body ${showBatchSection ? 'open' : ''}`}>
        <div>
        <div className="section-header">
          {isEditMode && !isManagingBatches && (
            <button className="btn-manage" onClick={() => setIsManagingBatches(true)}>
              + Add Batch
            </button>
          )}
        </div>

        <div className="batch-list">
          {template.batchMappings && template.batchMappings.length > 0 ? (
            template.batchMappings.map((batch: any) => (
              <div key={batch.batchId} className="batch-tag">
                <span className="batch-info">
                  <strong>{batch.batchCode || batch.batchName}</strong>
                  {batch.batchName && batch.batchCode && ` - ${batch.batchName}`}
                </span>
                {isEditMode && (
                  <button
                    className="remove-btn"
                    onClick={() => handleRemoveBatch(batch.batchId)}
                    title="Remove batch"
                  >
                    ×
                  </button>
                )}
              </div>
            ))
          ) : (
            <div className="no-batches">
              No batches associated with this template.
              {isEditMode && ' Click "Add Batch" to associate batches.'}
            </div>
          )}
        </div>

        {isManagingBatches && isEditMode && (
          <div className="batch-search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                value={batchSearch}
                onChange={(e) => handleBatchSearch(e.target.value)}
                placeholder="Search batches by name or code..."
                className="batch-search-input"
                autoFocus
              />
              <button
                className="close-search-btn"
                onClick={() => {
                  setIsManagingBatches(false);
                  setBatchSearch('');
                  setShowBatchDropdown(false);
                }}
              >
                Cancel
              </button>
            </div>

            {showBatchDropdown && batchOptions.length > 0 && (
              <div className="autocomplete-dropdown">
                {batchOptions.map(batch => (
                  <div
                    key={batch.id}
                    className="autocomplete-option"
                    onClick={() => handleAddBatch(batch)}
                  >
                    <strong>{batch.code || batch.name}</strong>
                    {batch.name && batch.code && <span> - {batch.name}</span>}
                  </div>
                ))}
              </div>
            )}

            {batchSearch.length >= 2 && batchOptions.length === 0 && !showBatchDropdown && (
              <div className="no-results">No batches found matching "{batchSearch}"</div>
            )}
          </div>
        )}
        </div>
        </div>
      </div>

      {/* Template Analytics Overview */}
      <TemplateAnalytics
        slots={template.activitySlotTemplates || []}
        batchMappings={template.batchMappings || []}
        templateStartDate={template.startDate}
        templateEndDate={template.endDate}
      />

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={activeTab === 'pattern' ? 'tab-button active' : 'tab-button'}
          onClick={() => setActiveTab('pattern')}
        >
          📐 Design Pattern
        </button>
        <button
          className={activeTab === 'slots' ? 'tab-button active' : 'tab-button'}
          onClick={() => setActiveTab('slots')}
        >
          📋 Manage Slots {template.activitySlotTemplates?.length > 0 && `(${template.activitySlotTemplates.length})`}
        </button>
        <button
          className={activeTab === 'visual' ? 'tab-button active' : 'tab-button'}
          onClick={() => setActiveTab('visual')}
        >
          📊 Visual Overview
        </button>
        <button
          className={activeTab === 'conducted' ? 'tab-button active' : 'tab-button'}
          onClick={() => setActiveTab('conducted')}
        >
          🎯 Conducted View
        </button>
        {template.macroPlans && template.macroPlans.length > 0 && (
          <button
            className={activeTab === 'macroplan' ? 'tab-button active' : 'tab-button'}
            onClick={() => setActiveTab('macroplan')}
          >
            🎯 Macro Plan ({template.macroPlans.length})
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'pattern' && (
        <WeekPatternGrid
          templateId={template.id}
          template={template}
          onGenerate={handleGenerate}
          onImport={() => { setImportType('pattern'); setShowImportModal(true); }}
        />
      )}

      {activeTab === 'slots' && (
        <>
          {isEditMode && permissions.canEditTemplate && (
            <div className="slots-tab-actions">
              <button
                className="btn btn-secondary"
                onClick={() => { setImportType('slots'); setShowImportModal(true); }}
              >
                Import from Excel
              </button>
            </div>
          )}
          <SlotFiltersPanel
            slots={template.activitySlotTemplates || []}
            templateStartDate={template.startDate}
            templateEndDate={template.endDate}
            onFilteredSlotsChange={setFilteredSlotsForList}
          >
            {({ filteredSlots, filtersActive, groupByMode, groups }) => (
              <>
                {groupByMode !== 'all' ? (
                  <div className="grouped-slot-view">
                    {groups.map((group) => (
                      <div key={group.key} className="slot-group-section">
                        <div className="slot-group-header">
                          <span className="slot-group-icon">{group.icon}</span>
                          <h3 className="slot-group-title">{group.label}</h3>
                          <span className="slot-group-count">{group.count} slots</span>
                        </div>
                        <LinearSlotList
                          slots={group.slots.map((s: ActivitySlot) => decoratedSlots.find(d => d.id === s.id) || s)}
                          isEditMode={isEditMode && permissions.canEditTemplate}
                          onEdit={handleEditSlot}
                          onDelete={handleDeleteSlot}
                          onBulkDelete={handleBulkDeleteSlots}
                          onSwap={handleSwapRequest}
                          onAdd={handleAddSlot}
                          templateBatchIds={(template.batchMappings || []).map((b: any) => b.batchId).filter(Boolean)}
                          batchMappings={template.batchMappings || []}
                          onAttendance={permissions.canTakeAttendance ? openAttendanceJava : undefined}
                          onAttendanceNode={permissions.canTakeAttendance ? openAttendanceNode : undefined}
                          onSubstitute={(slot) => setSubstituteSlot(slot as ActivitySlot)}
                          onConfirm={handleConfirmSlot}
                          onUnconfirm={handleUnconfirmSlot}
                        />
                      </div>
                    ))}
                    {groups.length === 0 && (
                      <div style={{ padding: '48px', textAlign: 'center', color: '#9e9e9e' }}>
                        No slots found matching the current filters.
                      </div>
                    )}
                  </div>
                ) : (
                  <LinearSlotList
                    slots={(filtersActive ? filteredSlots : decoratedSlots).map((s: ActivitySlot) => decoratedSlots.find(d => d.id === s.id) || s)}
                    isEditMode={isEditMode && permissions.canEditTemplate}
                    onEdit={handleEditSlot}
                    onDelete={handleDeleteSlot}
                    onBulkDelete={handleBulkDeleteSlots}
                    onSwap={handleSwapRequest}
                    onAdd={handleAddSlot}
                    templateBatchIds={(template.batchMappings || []).map((b: any) => b.batchId).filter(Boolean)}
                    batchMappings={template.batchMappings || []}
                    onAttendance={permissions.canTakeAttendance ? openAttendanceJava : undefined}
                    onAttendanceNode={permissions.canTakeAttendance ? openAttendanceNode : undefined}
                    onSubstitute={(slot) => setSubstituteSlot(slot as ActivitySlot)}
                    onConfirm={handleConfirmSlot}
                    onUnconfirm={handleUnconfirmSlot}
                  />
                )}
              </>
            )}
          </SlotFiltersPanel>
        </>
      )}

      {activeTab === 'visual' && (
        <TemplateBirdsEyeView
          slots={decoratedSlots}
          templateId={template.id}
          templateStartDate={template.startDate}
          templateEndDate={template.endDate}
          isEditMode={isEditMode && permissions.canEditTemplate}
          onAddSlot={handleAddSlot}
          onEditSlot={handleEditSlot}
          onDeleteSlot={handleDeleteSlot}
          onSlotMoved={handleSlotMoved}
          templateBatchIds={(template.batchMappings || []).map((b: any) => b.batchId).filter(Boolean)}
          batchMappings={template.batchMappings || []}
          conflictGroups={conflictGroups}
          onAttendance={permissions.canTakeAttendance ? openAttendanceJava : undefined}
          onAttendanceNode={permissions.canTakeAttendance ? openAttendanceNode : undefined}
          onSubstitute={(slot) => setSubstituteSlot(slot as ActivitySlot)}
        />
      )}

      {substituteSlot && (
        <SubstituteModal
          show={!!substituteSlot}
          activitySlotTemplateId={substituteSlot.id}
          templateName={substituteSlot.topicName || `Slot #${substituteSlot.id}`}
          templateStartTime={substituteSlot.startTime}
          templateEndTime={substituteSlot.endTime}
          originalInstructorId={substituteSlot.instructorId}
          originalInstructorName={substituteSlot.instructorName}
          defaultDate={substituteSlot.date ? String(substituteSlot.date).slice(0, 10) : undefined}
          mode="approve"
          onClose={() => setSubstituteSlot(null)}
          onSaved={() => {
            setSubstituteSlot(null);
          }}
        />
      )}

      {activeTab === 'conducted' && (
        <div className="conducted-tab" style={{ marginTop: 16 }}>
          <TemplateConductedView templateId={template.id} />
        </div>
      )}

      {/* Excel Import Modal */}
      {showImportModal && (
        <ExcelImportModal
          templateId={template.id}
          template={{ startDate: template.startDate, endDate: template.endDate }}
          importType={importType}
          onClose={() => setShowImportModal(false)}
          onSuccess={() => fetchTemplateDetail()}
        />
      )}

      {/* Extend Timetable Modal */}
      {showExtendModal && template && (
        <div className="modal-overlay" role="presentation">
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="extend-timetable-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="extend-timetable-title">Extend Timetable</h3>
              <button className="modal-close" onClick={() => setShowExtendModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--surface-sunken)', borderRadius: '6px', fontSize: '14px', color: '#424242' }}>
                <strong>Current date range:</strong><br />
                {formatDate(template.startDate)} → {formatDate(template.endDate)}
              </div>

              <div className="form-field">
                <label>New End Date</label>
                <input
                  type="date"
                  value={newEndDate}
                  min={template.endDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '16px 0', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={generateOnExtend}
                  onChange={(e) => setGenerateOnExtend(e.target.checked)}
                />
                Generate slots from weekly pattern for the extended period
              </label>

              {newEndDate && newEndDate > template.endDate && (
                <div style={{ padding: '10px 12px', background: '#e3f2fd', borderRadius: '6px', fontSize: '13px', color: '#1565c0' }}>
                  {generateOnExtend ? (
                    <>Existing slots will not be affected. New slots will be generated from <strong>{formatDate((() => { const d = new Date(template.endDate); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })())}</strong> → <strong>{formatDate(newEndDate)}</strong>.</>
                  ) : (
                    <>The template end date will be updated to <strong>{formatDate(newEndDate)}</strong>. No slots will be generated.</>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowExtendModal(false)}>Cancel</button>
              <button className="btn-save" onClick={handleExtend}>Extend</button>
            </div>
          </div>
        </div>
      )}

      {/* Swap Confirmation Modal */}
      {swapSlotIds && template && (() => {
        const slotA = (template.activitySlotTemplates || []).find(s => s.id === swapSlotIds[0]);
        const slotB = (template.activitySlotTemplates || []).find(s => s.id === swapSlotIds[1]);
        if (!slotA || !slotB) return null;

        const formatSlotDate = (d: string) => d ? new Date(d).toLocaleDateString('en-GB') : '';
        const formatSlotTime = (t: string) => t ? t.substring(0, 5) : '';

        return (
          <div className="modal-overlay" role="presentation" onClick={() => setSwapSlotIds(null)}>
            <div className="modal-content swap-modal" role="dialog" aria-modal="true" aria-labelledby="swap-slots-title" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 id="swap-slots-title">Swap Slots</h2>
              </div>
              <div className="modal-body">
                <div className="swap-slots-preview">
                  <div className="swap-slot-card">
                    <div style={{fontWeight: 600, marginBottom: 8}}>{formatSlotDate(slotA.date)} {formatSlotTime(slotA.startTime)}-{formatSlotTime(slotA.endTime)}</div>
                    <div>Instructor: {slotA.instructorName || '-'}</div>
                    <div>Topic: {slotA.topicName || '-'}</div>
                    {slotA.agenda && <div style={{color: '#9e9e9e', fontSize: 13}}>Agenda: {slotA.agenda}</div>}
                  </div>
                  <div className="swap-arrow">&#8644;</div>
                  <div className="swap-slot-card">
                    <div style={{fontWeight: 600, marginBottom: 8}}>{formatSlotDate(slotB.date)} {formatSlotTime(slotB.startTime)}-{formatSlotTime(slotB.endTime)}</div>
                    <div>Instructor: {slotB.instructorName || '-'}</div>
                    <div>Topic: {slotB.topicName || '-'}</div>
                    {slotB.agenda && <div style={{color: '#9e9e9e', fontSize: 13}}>Agenda: {slotB.agenda}</div>}
                  </div>
                </div>
                <p style={{color: '#616161', fontSize: 14, margin: '0 0 12px 0'}}>
                  The instructor, topic, and batches will be exchanged between these two slots.
                </p>
                <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14}}>
                  <input type="checkbox" checked={swapIncludeRoom} onChange={(e) => setSwapIncludeRoom(e.target.checked)} style={{width: 16, height: 16}} />
                  Also swap rooms
                </label>
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => setSwapSlotIds(null)}>Cancel</button>
                <button className="btn-save" onClick={handleSwapConfirm}>Swap</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {/* Activity Slot Modal */}
      {showSlotModal && (
        <div className="modal-overlay" role="presentation">
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="activity-slot-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="activity-slot-title">{editingSlot ? 'Edit Activity Slot' : 'Add Activity Slot'}</h3>
              <button className="modal-close" onClick={() => setShowSlotModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-field">
                <label>Date *</label>
                <input
                  type="date"
                  name="date"
                  value={slotFormData.date}
                  onChange={handleSlotFormChange}
                  min={template?.startDate}
                  max={template?.endDate}
                  required
                />
              </div>
              {slotFormData.date && (
                <div className="form-field checkbox-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={slotFormData.repeatWeekly}
                      onChange={(e) => setSlotFormData(prev => ({
                        ...prev,
                        repeatWeekly: e.target.checked
                      }))}
                    />
                    {(() => {
                      const d = new Date(slotFormData.date + 'T00:00:00');
                      const dayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][d.getDay()];
                      return editingSlot
                        ? `Apply changes to all matching future ${dayName} slots`
                        : `Repeat every ${dayName} until ${template ? formatDate(template.endDate) : 'end date'}`;
                    })()}
                  </label>
                </div>
              )}
              <div className="form-row">
                <div className="form-field">
                  <label>Start Time *</label>
                  <input
                    type="time"
                    name="startTime"
                    value={slotFormData.startTime}
                    onChange={handleSlotFormChange}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>End Time *</label>
                  <input
                    type="time"
                    name="endTime"
                    value={slotFormData.endTime}
                    onChange={handleSlotFormChange}
                    required
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Batch(es)</label>
                <div className="batch-tags">
                  {template?.batchMappings?.map((batch: any) => (
                    <label key={batch.batchId} className="batch-tag">
                      <input
                        type="checkbox"
                        checked={slotFormData.batchIds.includes(batch.batchId)}
                        onChange={(e) => handleSlotBatchToggle(batch.batchId, e.target.checked)}
                      />
                      {batch.batchName || `Batch ${batch.batchId}`}
                    </label>
                  ))}
                </div>
                {(!template?.batchMappings || template.batchMappings.length === 0) && (
                  <div className="no-batches">No batches associated with this template</div>
                )}
              </div>
              {/* Instructor/Topic fields - disabled until at least one batch is picked.
                  Toggling batches resets these values so they always reflect the chosen batches. */}
              {(!slotFormData.batchIds || slotFormData.batchIds.length === 0) ? (
                <>
                  <div className="form-field autocomplete-field">
                    <label>Instructor</label>
                    <input type="text" placeholder="Select a batch first" disabled />
                  </div>
                  <div className="form-field autocomplete-field">
                    <label>Topic</label>
                    <input type="text" placeholder="Select a batch first" disabled />
                  </div>
                </>
              ) : (() => {
                const instructorField = (
                  <div className="form-field autocomplete-field" key="instructor">
                    <label>Instructor</label>
                    {slotFormData.instructorId ? (
                      <div className="selected-value">
                        <span>{slotFormData.instructorName}</span>
                        <button className="clear-btn" onClick={clearInstructor} title="Clear">×</button>
                      </div>
                    ) : filteredInstructors.length > 0 ? (
                      <>
                        <select
                          value=""
                          onChange={(e) => {
                            const instructor = filteredInstructors.find((i: any) => i.id.toString() === e.target.value);
                            if (instructor) selectInstructor(instructor);
                          }}
                        >
                          <option value="">-- Select Instructor --</option>
                          {filteredInstructors.map((instructor: any) => (
                            <option key={instructor.id} value={instructor.id}>
                              {instructor.name || `${instructor.firstName} ${instructor.lastName}`}
                              {instructor.code ? ` (${instructor.code})` : ''}
                            </option>
                          ))}
                        </select>
                        <div className="field-hint">Filtered by selected topic</div>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={instructorSearch}
                          onChange={(e) => handleInstructorSearch(e.target.value)}
                          onFocus={() => instructorSearch.length >= 2 && setShowInstructorDropdown(true)}
                          placeholder="Type to search instructor..."
                        />
                        {showInstructorDropdown && (
                          <div className="autocomplete-dropdown" onMouseDown={(e) => e.preventDefault()}>
                            {instructorSearchLoading ? (
                              <div className="autocomplete-item loading">
                                <div className="item-name">Searching...</div>
                              </div>
                            ) : instructorOptions.length > 0 ? (
                              instructorOptions.map((instructor) => (
                                <div
                                  key={instructor.id}
                                  className="autocomplete-item"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    selectInstructor(instructor);
                                  }}
                                >
                                  <div className="item-name">
                                    {instructor.name || `${instructor.firstName} ${instructor.lastName}`}
                                  </div>
                                  {instructor.code && (
                                    <div className="item-meta">{instructor.code}</div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="autocomplete-item no-results">
                                <div className="item-name">No instructors found matching "{instructorSearch}"</div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );

                const topicField = (
                  <div className="form-field autocomplete-field" key="topic">
                    <label>Topic</label>
                    {slotFormData.topicMasterId ? (
                      <div className="selected-value">
                        <span>{slotFormData.topicName}</span>
                        <button className="clear-btn" onClick={clearTopic} title="Clear">×</button>
                      </div>
                    ) : filteredTopics.length > 0 ? (
                      <>
                        <select
                          value=""
                          onChange={(e) => {
                            const topic = filteredTopics.find((t: any) => t.id.toString() === e.target.value);
                            if (topic) selectTopic(topic);
                          }}
                        >
                          <option value="">-- Select Topic --</option>
                          {filteredTopics.map((topic: any) => (
                            <option key={topic.id} value={topic.id}>
                              {topic.name}
                              {topic.code ? ` (${topic.code})` : ''}
                            </option>
                          ))}
                        </select>
                        <div className="field-hint">Filtered by selected instructor</div>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={topicSearch}
                          onChange={(e) => handleTopicSearch(e.target.value)}
                          onFocus={() => topicSearch.length >= 2 && setShowTopicDropdown(true)}
                          placeholder="Type to search topic..."
                        />
                        {showTopicDropdown && (
                          <div className="autocomplete-dropdown" onMouseDown={(e) => e.preventDefault()}>
                            {topicSearchLoading ? (
                              <div className="autocomplete-item loading">
                                <div className="item-name">Searching...</div>
                              </div>
                            ) : topicOptions.length > 0 ? (
                              topicOptions.map((topic) => (
                                <div
                                  key={topic.id}
                                  className="autocomplete-item"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    selectTopic(topic);
                                  }}
                                >
                                  <div className="item-name">{topic.name}</div>
                                  {topic.code && (
                                    <div className="item-meta">{topic.code}</div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="autocomplete-item no-results">
                                <div className="item-name">No topics found matching "{topicSearch}"</div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );

                return firstSelected === 'topic'
                  ? <>{topicField}{instructorField}</>
                  : <>{instructorField}{topicField}</>;
              })()}
              <div className="form-field">
                <label>Room</label>
                <select
                  value={slotFormData.roomId}
                  onChange={(e) => {
                    const id = e.target.value;
                    const room = roomOptions.find((r: any) => r.id.toString() === id);
                    setSlotFormData(prev => ({
                      ...prev,
                      roomId: id,
                      roomName: room?.name || '',
                    }));
                  }}
                >
                  <option value="">-- No Room --</option>
                  {roomOptions.map((room: any) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                      {room.centreName ? ` — ${room.centreName}` : ''}
                      {room.capacity ? ` (cap ${room.capacity})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field checkbox-field">
                <label>
                  <input
                    type="checkbox"
                    checked={slotFormData.onlineMeeting}
                    onChange={(e) => setSlotFormData(prev => ({
                      ...prev,
                      onlineMeeting: e.target.checked
                    }))}
                  />
                  Online Meeting
                </label>
                {slotFormData.onlineMeeting && (
                  <>
                    <div style={{marginTop: '8px'}}>
                      <label style={{fontSize: '14px', display: 'block', marginBottom: '4px'}}>Meeting Type</label>
                      <select
                        value={slotFormData.onlineMeetingType}
                        onChange={(e) => setSlotFormData(prev => ({
                          ...prev,
                          onlineMeetingType: e.target.value
                        }))}
                        style={{width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid var(--border-default)'}}
                      >
                        <option value="">-- Select Meeting Type --</option>
                        {meetingTypeOptions.map((type: any) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="text"
                      name="zoomMeetingId"
                      value={slotFormData.zoomMeetingId}
                      onChange={handleSlotFormChange}
                      placeholder="Meeting ID (e.g., Zoom Meeting ID, Webinar ID, Teams Meeting ID)"
                      style={{marginTop: '8px', width: '100%'}}
                    />
                  </>
                )}
              </div>
              <div className="form-field">
                <label>Agenda</label>
                <textarea
                  name="agenda"
                  value={slotFormData.agenda}
                  onChange={handleSlotFormChange}
                  rows={3}
                  placeholder="Enter agenda or notes"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowSlotModal(false)}>
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleSaveSlot}
                disabled={isSavingSlot || (!!editingSlot && !isSlotFormDirty)}
              >
                {isSavingSlot
                  ? (editingSlot ? 'Updating…' : 'Adding…')
                  : (editingSlot ? 'Update Slot' : 'Add Slot')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal — rendered LAST so it stacks on top of any other
          still-open modal (e.g. the Activity Slot modal after a save fails
          with 409, so the user can still read the conflict message). */}
      {alertModal && (
        <AlertModal
          title={alertModal.title}
          message={alertModal.message}
          type={alertModal.type}
          actionUrl={alertModal.actionUrl}
          actionLabel={alertModal.actionLabel}
          onClose={() => setAlertModal(null)}
        />
      )}
    </div>
  );
};

export default TemplateDetail;
